import React from 'react';
import styled, { css } from 'styled-components';

import { ModalContainer as _ModalContainer, ModalFooter as _ModalFooter } from 'src/components/core';
import { ModalWrapper } from 'shared/modals/v2-wrapper';

import { QRCodeSVG } from 'qrcode.react';
import LoadingSpinner from 'src/assets/loader/spinner';
import { ModalClose, ModalPrimaryHeader as _ModalPrimaryHeader } from 'shared/modals';

type DposQrCodeProps = {
  onClose: () => void;
  referralUrl: string | null;
  loading: boolean;
};

export const DposReferralQrCode = ({ referralUrl, loading, onClose }: DposQrCodeProps): JSX.Element | null => (
  <ModalWrapper open>
    <ModalContainer>
      <ModalPrimaryHeader close={onClose} copy='Scan the code' />
      <ModalBody>
        <QrCodeContainer>
          {referralUrl && !loading ? (
            <QRCodeSVG data-testid='referral-qr-code' value={referralUrl} size={300} />
          ) : (
            <Loader data-testid='loading-indicator'>
              <LoadingSpinner size={100} borderColor='#f3f6f8' />
            </Loader>
          )}
        </QrCodeContainer>
      </ModalBody>
      <ModalFooter>Use your phone's camera to scan the code above.</ModalFooter>
      <ModalClose onClick={onClose} />
    </ModalContainer>
  </ModalWrapper>
);

const ModalContainer = styled(_ModalContainer)`
  position: relative;
  padding: 0;
  /*
   * A fixed 356px (plus the 20px side margins below) is wider than a small
   * phone viewport such as 320px, which would push the modal off screen.
   * Cap the width instead so it shrinks to fit narrow screens.
   */
  width: 100%;
  max-width: 356px;
  z-index: 1000;
  justify-self: center;

  @media screen and (max-width: 900px) {
    /*
     * Bound the shrink by the 356px cap above. Without min(),
     * calc(100vw - 40px) exceeds 356px on mid-size viewports (e.g. 600px
     * or 768px tablets) and would inflate the modal past the compact card
     * width, leaving a large empty area around the fixed-size QR code.
     */
    max-width: min(356px, calc(100vw - 40px));
    margin: 0 20px;
  }
`;

const ModalPrimaryHeader = styled(_ModalPrimaryHeader)`
  padding: 24px;
  background-color: var(--dcm-referral-qr-modal-bg);
  border-bottom: none;

  h2 {
    color: var(--dcm-referral-qr-heading-color);
    font-size: 20px;
    font-weight: 600;
  }
`;

const ModalFooter = styled(_ModalFooter)`
  padding: 24px;
  background-color: var(--dcm-referral-qr-modal-bg);
`;
const ModalBody = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 24px;
`;

const QrCodeContainer = styled.div`
  ${({ theme: { spaces } }) => css`
    align-items: center;
    display: flex;
    flex-direction: column;
    gap: ${spaces[1]};
    justify-content: center;
    margin: ${spaces[6]} 0;
    color: var(--dcm-referral-qr-text-color);
    margin: 38px 0 0;
    /*
     * QRCodeSVG renders at a fixed 300px. Allow the rendered SVG to scale
     * down with the modal on narrow screens instead of overflowing it.
     */
    max-width: 100%;

    svg {
      max-width: 100%;
      height: auto;
    }
  `}
`;

const Loader = styled.div`
  align-items: center;
  border-radius: 100px;
  display: flex;
  height: 100px;
  justify-content: center;
  width: 100px;
`;
